import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    FormGroup,
    FormControlLabel,
    Checkbox,
    TextField,
    Button,
    Alert,
    Snackbar,
    Divider,
    Card,
    CardContent,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Assignment as AssignmentIcon,
    Gavel as GavelIcon,
    Security as SecurityIcon,
    Description as DescriptionIcon,
    CloudUpload as UploadIcon,
    Visibility as ViewIcon,
    Save as SaveIcon,
    Send as SendIcon,
    Warning as WarningIcon,
    GetApp as DownloadIcon,
    Payment as PaymentIcon,
    Lock as LockIcon,
    Error as ErrorIcon
} from '@mui/icons-material';
import FileUploadComponent from '../../../components/FileUploadComponent';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import authService from '../../../services/authService';
import paymentService from '../../../services/paymentService';
import { decryptText, decryptAppId } from '../../../utils/encryption';
import FeesService from '../../../services/feesService';
import declarationService from '../../../services/declarationService';
import LaboratoryService from '../../../services/laboratoryService';
import s3Utils from '../../../utils/s3Utils';
import previewReportService from '../../../services/previewReportService';
import {
    insertDANC,
    updateDANC,
    getDANCById
} from '../../../services/apiCalibration';
import { encryptAppId, encryptText } from '../../../utils/encryption';
import axios from 'axios';

// Adequacy Check Component with DA NC Integration
const AdequacyCheckSection = React.memo(({
    sectionId,
    title,
    applicationStage,
    orgId,
    appId,
    adequacyChecks,
    handleAdequacyCheck,
    handleAdequacyRemarks,
    handleAdequacySubmit,
    loading,
    currentUser = 'System User',
    currentUserId = 0
}) => {
    // Local state for DA NC operations
    const [dancLoading, setDancLoading] = useState(false);
    const [dancError, setDancError] = useState('');
    const [dancSuccess, setDancSuccess] = useState('');
    const [existingDANCRecord, setExistingDANCRecord] = useState(null);
    const [dataLoaded, setDataLoaded] = useState(false);
    

    // Load existing DA NC data on component mount
    useEffect(() => {
        const loadExistingDANCData = async () => {
            if (!orgId || !appId) return;

            try {
                setDancLoading(true);
                const response = await getDANCById(orgId, appId, title);

                if (response.data.success && response.data.data) {
                    setExistingDANCRecord(response.data.data);

                    // Bind DA NC data to adequacy check fields
                    if (response.data.data.InformationStatus) {
                        handleAdequacyCheck(sectionId, response.data.data.InformationStatus);
                    }

                    const remarks = response.data.data.InformationProvided || response.data.data.Observation || '';
                    if (remarks) {
                        handleAdequacyRemarks(sectionId, remarks);
                    }
                }
                setDataLoaded(true);
            } catch (error) {
                setDataLoaded(true);
            } finally {
                setDancLoading(false);
            }
        };

        if (!dataLoaded) {
            loadExistingDANCData();
        }
    }, [orgId, appId, title, sectionId, dataLoaded, handleAdequacyCheck, handleAdequacyRemarks]);

    // Clear messages after timeout
    useEffect(() => {
        if (dancError || dancSuccess) {
            const timer = setTimeout(() => {
                setDancError('');
                setDancSuccess('');
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [dancError, dancSuccess]);

    // Only show if applicationStage is 18, 18b or 18a
    if (applicationStage !== "18" && applicationStage !== "18a" && applicationStage !== "18b") return null;

    // Check if adequacy checks should be read-only (stage 18a)
    const isAdequacyReadOnly = applicationStage === "18a";

    const adequacy = adequacyChecks[sectionId] || { status: '', remarks: '' };

    // Standard status change handler
    const handleStatusChange = (status) => {
        handleAdequacyCheck(sectionId, status);
    };

    // Standard remarks change handler
    const handleRemarksChange = (remarks) => {
        handleAdequacyRemarks(sectionId, remarks);
    };

    // Enhanced submit handler with DA NC API call
    const handleSubmit = async () => {
        try {
            setDancLoading(true);
            setDancError('');
            setDancSuccess('');

            // First submit the adequacy check
            await handleAdequacySubmit(sectionId);

            // Then create/update DA NC record based on status
            if (adequacy.status) {
                if (existingDANCRecord) {
                    // Update existing record
                    try {
                        const updateData = {
                            subsectionName: sectionId,
                            informationStatus: adequacy.status,
                            observation: adequacy.status === 'Inadequate'
                                ? (adequacy.remarks || 'Document adequacy marked as Inadequate')
                                : 'Document adequacy check completed',
                            isactive: 1
                        };

                        const response = await updateDANC(orgId, appId, title, updateData);

                        if (response.success) {
                            setExistingDANCRecord(response.data);
                        } else {
                            throw new Error(response.message || 'Update failed');
                        }
                    } catch (updateError) {
                        console.error('DA-NC update failed:', updateError);
                    }
                } else {
                    // Create new record  
                    try {
                        const dancData = {
                            Orgid: parseInt(orgId),
                            Appid: parseInt(appId),
                            SectionName: title,
                            SubsectionName: sectionId,
                            InformationProvided: adequacy.remarks || '',
                            InformationStatus: adequacy.status,
                            Observation: adequacy.status === 'Inadequate'
                                ? (adequacy.remarks || 'Document adequacy marked as Inadequate')
                                : 'Document adequacy check completed',
                            Insertedby: currentUserId,
                            NCtype: 'Document Adequacy'
                        };

                        const response = await insertDANC(dancData);

                        if (response.data.success) {
                            setExistingDANCRecord(response.data.data);
                            setDancSuccess('Record created successfully');
                        } else {
                            throw new Error(response.message || 'Insert failed');
                        }
                    } catch (insertError) {
                        console.error('DA-NC creation failed:', insertError);
                        setDancError('Failed to create Record: ' + insertError.message);
                    }
                }
            }
        } catch (error) {
            console.error('Error submitting adequacy check:', error);
            setDancError('Error submitting adequacy check: ' + error.message);
        } finally {
            setDancLoading(false);
        }
    };

    return (
        <Box sx={{
            mb: 3,
            p: 3,
            backgroundColor: '#f8f9fa',
            borderRadius: 2,
            border: '2px solid #e9ecef',
            borderLeft: '4px solid #007bff'
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{
                    color: '#495057',
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}>
                    📋 Adequacy Check - {title}
                    {isAdequacyReadOnly && (
                        <Chip
                            label="🔒 Read Only"
                            size="small"
                            sx={{
                                bgcolor: '#f5f5f5',
                                color: '#757575',
                                fontWeight: 'bold',
                                fontSize: '0.7rem'
                            }}
                        />
                    )}
                </Typography>

                {(adequacy.status || existingDANCRecord) && (
                    <Chip
                        label={`DA-NC: ${adequacy.status || existingDANCRecord?.InformationStatus || 'Active'}`}
                        size="small"
                        sx={{
                            bgcolor: (adequacy.status || existingDANCRecord?.InformationStatus) === 'Adequate' ? '#28a745' : '#dc3545',
                            color: 'white',
                            fontWeight: 'bold'
                        }}
                    />
                )}
            </Box>

            {/* DA NC Error/Success Messages */}
            {dancError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDancError('')}>
                    {dancError}
                </Alert>
            )}

            {dancSuccess && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setDancSuccess('')}>
                    {dancSuccess}
                </Alert>
            )}

            <Grid container spacing={3} alignItems="flex-start">
                <Grid item xs={12} md={4}>
                    <FormControl fullWidth sx={{
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white',
                            borderRadius: 2,
                            '&:hover': {
                                boxShadow: '0 2px 8px rgba(0,123,255,0.15)',
                            },
                            '&.Mui-focused': {
                                boxShadow: '0 4px 12px rgba(0,123,255,0.25)',
                            }
                        }
                    }}>
                        <InputLabel>Document Status</InputLabel>
                        <Select
                            value={adequacy.status}
                            onChange={isAdequacyReadOnly ? undefined : (e) => handleStatusChange(e.target.value)}
                            label="Document Status"
                            disabled={isAdequacyReadOnly}
                            sx={isAdequacyReadOnly ? {
                                backgroundColor: '#f5f5f5',
                                cursor: 'not-allowed',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#e0e0e0',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#e0e0e0',
                                }
                            } : undefined}
                        >
                            <MenuItem value="">-- Select Status --</MenuItem>
                            <MenuItem value="Adequate">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CheckCircleIcon sx={{ color: '#28a745', fontSize: '1rem' }} />
                                    Adequate
                                </Box>
                            </MenuItem>
                            <MenuItem value="Inadequate">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <ErrorIcon sx={{ color: '#dc3545', fontSize: '1rem' }} />
                                    Inadequate
                                </Box>
                            </MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                {adequacy.status === 'Inadequate' && (
                    <Grid item xs={12} md={8}>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label={isAdequacyReadOnly ? "Remarks (View Only)" : "Remarks (Required)"}
                            placeholder={isAdequacyReadOnly ? "View Only (Read-only)" : "Please provide specific comments about the inadequacies..."}
                            value={adequacy.remarks || ''}
                            onChange={isAdequacyReadOnly ? undefined : (e) => handleRemarksChange(e.target.value)}
                            required={!isAdequacyReadOnly}
                            variant="outlined"
                            InputProps={isAdequacyReadOnly ? {
                                readOnly: true,
                                style: {
                                    backgroundColor: '#f5f5f5',
                                    cursor: 'not-allowed'
                                }
                            } : undefined}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: isAdequacyReadOnly ? '#f5f5f5' : 'white',
                                    borderRadius: 2,
                                    cursor: isAdequacyReadOnly ? 'not-allowed' : 'text',
                                    '&:hover': {
                                        boxShadow: isAdequacyReadOnly ? 'none' : '0 2px 8px rgba(220,53,69,0.15)',
                                    },
                                    '&.Mui-focused': {
                                        boxShadow: isAdequacyReadOnly ? 'none' : '0 4px 12px rgba(220,53,69,0.25)',
                                    }
                                }
                            }}
                        />
                        <Typography variant="caption" sx={{
                            color: '#6c757d',
                            display: 'block',
                            mt: 1,
                            fontStyle: 'italic'
                        }}>
                            {isAdequacyReadOnly
                                ? "Adequacy check is in read-only mode for stage 18a."
                                : "Please specify what needs to be corrected or improved."
                            }
                        </Typography>
                    </Grid>
                )}
            </Grid>

            {adequacy.status && (
                <Box sx={{ mt: 2 }}>
                    <Alert
                        severity={existingDANCRecord
                            ? (adequacy.status === 'Adequate' ? 'info' : 'warning')
                            : (adequacy.status === 'Adequate' ? 'success' : 'warning')
                        }
                        sx={{
                            borderRadius: 2,
                            '& .MuiAlert-icon': {
                                fontSize: '1.2rem'
                            }
                        }}
                    >
                        {isAdequacyReadOnly
                            ? '🔒 Adequacy check is in read-only mode for stage 18a. No modifications can be made.'
                            : (existingDANCRecord
                                ? (adequacy.status === 'Adequate'
                                    ? `✓ Record exists. Click Update to modify.`
                                    : `⚠ Record exists. Click Update to modify.`)
                                : (adequacy.status === 'Adequate'
                                    ? '✓ This section is marked as Adequate. Click Submit to create Record.'
                                    : '⚠ Please address the remarks above and submit to create Record.'))}
                    </Alert>

                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            size="medium"
                            onClick={handleSubmit}
                            disabled={
                                isAdequacyReadOnly ||
                                !adequacy.status ||
                                (adequacy.status === 'Inadequate' && !adequacy.remarks?.trim()) ||
                                loading ||
                                dancLoading
                            }
                            sx={{
                                background: existingDANCRecord
                                    ? 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)'
                                    : (adequacy.status === 'Adequate'
                                        ? 'linear-gradient(135deg, #28a745 0%, #20c997 100%)'
                                        : 'linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)'),
                                color: 'white',
                                fontWeight: 600,
                                px: 3,
                                py: 1,
                                borderRadius: 2,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                                },
                                '&:disabled': {
                                    background: '#e9ecef',
                                    color: '#6c757d',
                                    transform: 'none',
                                    boxShadow: 'none'
                                }
                            }}
                        >
                            {(loading || dancLoading) ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={16} sx={{ color: 'white' }} />
                                    {existingDANCRecord ? 'Updating...' : 'Submitting...'}
                                </Box>
                            ) : (
                                <>📤 {existingDANCRecord ? 'Update Adequacy Check' : 'Submit Adequacy Check'}</>
                            )}
                        </Button>
                    </Box>
                </Box>
            )}
        </Box>
    );
});

const DeclarationSection = ({ data, onUpdate }) => {
    const navigate = useNavigate();

    // Router hooks for encrypted parameters
    const { orgId: encryptedOrgId, appId: encryptedAppId } = useParams();
    const location = useLocation();
    const [canProceedToPay, setCanProceedToPay] = useState(false);

    // Extract encrypted parameters from URL query params or route params
    const getOrgId = useCallback(() => {
        try {
            // First try to get from URL query parameters
            const urlParams = new URLSearchParams(location.search);
            const orgIdParam = urlParams.get('orgId');
            if (orgIdParam) {
                return decryptText(orgIdParam);
            }

            // Then try to get from route parameters
            if (encryptedOrgId) {
                return decryptText(encryptedOrgId);
            }

            return null;
        } catch (error) {
            console.error('DeclarationSection - Error decrypting orgId:', error);
            return null;
        }
    }, [encryptedOrgId, location.search]);

    const getAppId = useCallback(() => {
        try {
            // First try to get from URL query parameters
            const urlParams = new URLSearchParams(location.search);
            const appIdParam = urlParams.get('appId');
            if (appIdParam) {
                return decryptAppId(appIdParam);
            }

            // Then try to get from route parameters
            if (encryptedAppId) {
                return decryptAppId(encryptedAppId);
            }

            return null;
        } catch (error) {
            console.error('DeclarationSection - Error decrypting appId:', error);
            return null;
        }
    }, [encryptedAppId, location.search]);

    // State management
    const [declarationData, setDeclarationData] = useState({
        // Authorized representative details
        representativeName: '',
        representativeTitle: '',

        // Additional declaration
        termchk: false,
        finalDeclarationAgreed: false,

        // File upload
        shareholderDirectorFile: null
    });

    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'info'
    });

    const [submitDialog, setSubmitDialog] = useState(false);
    const [passwordDialog, setPasswordDialog] = useState(false);
    const [password, setPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [loading, setLoading] = useState(false);
    const [feesData, setFeesData] = useState(null);
    const [feesLoading, setFeesLoading] = useState(false);

    // Simplified state management
    const [fieldsDisabled, setFieldsDisabled] = useState(false);
    const [paymentStatus, setPaymentStatus] = useState(null); // null, 'failed', 'success'
    const [showSaveButton, setShowSaveButton] = useState(false);
    const [showPaymentButtons, setShowPaymentButtons] = useState(false);
    const [showSubmitButton, setShowSubmitButton] = useState(false);
    // GST Exemption state
    const [gstExemptionStatus, setGstExemptionStatus] = useState(null); // null, 'approved', 'pending', 'not_requested'
    const [gstExemptionMessage, setGstExemptionMessage] = useState('');

    // Add loading flag to prevent multiple calls
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);
    const [applicationStage, setApplicationStage] = useState(null);

    // Adequacy Check States
    const [adequacyChecks, setAdequacyChecks] = useState({});
    const [adequacyLoading, setAdequacyLoading] = useState(false);
    const [isGstExemption, setIsGstExemption] = useState(false);

    // Adequacy Check Handlers
    const handleAdequacyCheck = (sectionId, status) => {
        setAdequacyChecks(prev => ({
            ...prev,
            [sectionId]: {
                ...(prev[sectionId] || {}),
                status: status
            }
        }));
    };

    const handleAdequacyRemarks = (sectionId, remarks) => {
        setAdequacyChecks(prev => ({
            ...prev,
            [sectionId]: {
                ...(prev[sectionId] || {}),
                remarks: remarks
            }
        }));
    };

    const handleAdequacySubmit = async (sectionId) => {
        setAdequacyLoading(true);
        return new Promise((resolve) => {
            setTimeout(() => {
                setAdequacyChecks(prev => ({
                    ...prev,
                    [sectionId]: {
                        ...prev[sectionId],
                        isSubmitted: true,
                        submittedAt: new Date().toISOString()
                    }
                }));
                setAdequacyLoading(false);
                resolve();
            }, 500);
        });
    };

    // Get current application stage
    const getCurrentApplicationStage = async () => {
        try {
            const appId = getAppId();
            if (appId) {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/laboratory/application-stage/${appId}`);
                const result = await response.json();

                if (result.success && result.stage) {
                    setApplicationStage(result.stage);

                } else {
                    setApplicationStage(null); // Stage not found, set to null

                }
            } else {
                setApplicationStage(null); // No appId, set to null
            }
        } catch (error) {
            console.error('Error determining application stage:', error);
            // Use null as default if API fails
            setApplicationStage(null);
        }
    };

    const labbasicdetails = async () => {
        try {
            const orgId = getOrgId();
            const appId = getAppId();
            if (!orgId || !appId) return;

            const response = await fetch(`${process.env.REACT_APP_API_URL}/laboratory/details/${orgId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            const result = await response.json();

            if (result.success && result.data) {

                setIsGstExemption(result.data.GSTExemption === true);

            } else {
                console.error('Error fetching laboratory details:', result.message);
            }
        } catch (error) {
            console.error('Error fetching laboratory details:', error);
        }
    };

    const checkGSTExemptionStatus = async () => {
        try {
            const appId = getAppId();
            if (!appId) {
                setGstExemptionStatus('not_requested');

                return;
            }

            const response = await fetch(`${process.env.REACT_APP_API_URL}/gst-exemption/check-status/${appId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            const result = await response.json();

            if (result.success && result.data && result.data.length > 0) {
                const exemptionRecord = result.data[0]; // Get the first record
                const isApproved = exemptionRecord.isapproved;
                const remarks = exemptionRecord.Remarks || '';

                if (isApproved === 1) {
                    // Approved
                    setGstExemptionStatus('approved');
                    setGstExemptionMessage('GST exemption approved');
                } else if (isApproved === 0) {
                    // Pending
                    setGstExemptionStatus('pending');
                    setGstExemptionMessage('Your GST exemption is pending in accounts section for approval');
                } else if (isApproved === 2) {
                    // Rejected
                    setGstExemptionStatus('rejected');
                    setGstExemptionMessage(`GST exemption has been rejected. Remarks: ${remarks}`);
                } else {
                    // Unknown status
                    setGstExemptionStatus('pending');
                    setGstExemptionMessage('GST exemption status is unknown. Please contact support.');
                }
            } else if (result.success && (!result.data || result.data.length === 0)) {
                // No GST exemption request found
                setGstExemptionStatus('not_requested');
                setGstExemptionMessage('No GST exemption request found');
            } else {
                // API error
                setGstExemptionStatus('pending');
                setGstExemptionMessage('Error checking GST exemption status');
            }
        } catch (error) {
            console.error('Error checking GST exemption status:', error);
            setGstExemptionStatus('pending');
            setGstExemptionMessage('Error checking GST exemption status');
        }
    };

    // Load declaration data and check payment status on page load
    useEffect(() => {
        const initializeComponent = async () => {

            await loadDeclarationData();
            await checkGSTExemptionStatus();
            await getCurrentApplicationStage();
            await labbasicdetails();

            // Check payment status immediately after loading other data
            await checkPaymentStatus();
            await checkPaymentCount()

            setInitialLoadComplete(true);
        };

        initializeComponent();
    }, [applicationStage]);

    // Load declaration data
    const loadDeclarationData = async () => {
        try {
            const orgId = getOrgId();
            const appId = getAppId();

            if (!orgId || !appId) return;

            // Get declaration data
            const response = await LaboratoryService.getDeclarationData(orgId, appId);

            if (response.success && response.data) {

                // Handle the case where both name and title are in DeclNameOrg
                let repName = '';
                let repTitle = '';

                if (response.data.DeclNameOrg) {
                    // Split the combined field "Ayan, head of laboratory"
                    const parts = response.data.DeclNameOrg.split(',').map(part => part.trim());
                    repName = parts[0] || '';
                    repTitle = parts[1] || '';
                } else {
                    // Fallback to separate fields if they exist
                    repName = response.data.representativeName || '';
                    repTitle = response.data.representativeTitle || '';
                }

                // Process shareholderDirectorFile with S3 utils (matching Laboratory section)
                let processedFile = null;
                if (response.data.shareholderDirectorFile) {
                    // If it's a string (filename), process it to get signed URL
                    if (typeof response.data.shareholderDirectorFile === 'string') {
                        processedFile = await s3Utils.processForm153FileData(
                            response.data.shareholderDirectorFile,
                            'shareholderDirectorFile'
                        );
                    } else {
                        // If it's already an object, use it as-is
                        processedFile = response.data.shareholderDirectorFile;
                    }
                }

                // Update form fields
                setDeclarationData({
                    representativeName: repName,
                    representativeTitle: repTitle,
                    shareholderDirectorFile: processedFile,
                    termchk: response.data.termchk || false,
                    finalDeclarationAgreed: response.data.finalDeclarationAgreed || false
                });

                // Create modified data object for button visibility check
                const modifiedData = {
                    representativeName: repName,
                    representativeTitle: repTitle,
                    shareholderDirectorFile: processedFile,
                    termchk: response.data.termchk || false,
                    finalDeclarationAgreed: response.data.finalDeclarationAgreed || false
                };

                // Check if all fields are filled and determine button state
                checkButtonVisibility(modifiedData);

                // Check payment status with loaded data
                await checkPaymentStatus(modifiedData);
            } else {
                // No existing data - show save button disabled initially
                setShowSaveButton(true);
                // Check payment status with empty data
                await checkPaymentStatus();
            }

        } catch (error) {

            setShowSaveButton(true);
            await checkPaymentStatus();
        }
    };

    // Check what buttons to show based on declaration data
    const checkButtonVisibility = (data = declarationData) => {
        const hasAllFields = data.representativeName &&
            data.representativeTitle &&
            data.shareholderDirectorFile &&
            data.finalDeclarationAgreed &&
            data.termchk;

        if (hasAllFields) {
            // All fields filled - disable fields and hide save button
            console.log('✅ All fields complete - disabling form fields');
            setFieldsDisabled(true);
            setShowSaveButton(false);
            // Payment buttons and Submit button will be determined by payment status check
        } else {
            // Some fields missing - show save button, enable fields
            console.log('❌ Missing fields - enabling form for editing');
            setShowSaveButton(true);
            setFieldsDisabled(false);
            setShowPaymentButtons(false);
            // Don't set Submit button here - let checkPaymentStatus handle it
        }
    };

    // Check payment status from PaymentGateWayTracking table
    const checkPaymentStatus = async (dataToCheck) => {
        // Always use current state if no data provided
        const currentData = dataToCheck || declarationData;

        console.log('🔄 checkPaymentStatus called with data:', currentData);

        // Skip if we don't have meaningful data yet (prevent premature hiding of buttons)
        const hasAnyData = currentData.representativeName || currentData.representativeTitle || currentData.shareholderDirectorFile;
        if (!dataToCheck && !hasAnyData) {
            console.log('⏭️ Skipping payment check - no meaningful data yet');
            return;
        }

        try {
            const orgId = getOrgId();
            const appId = getAppId();

            if (!orgId || !appId) {

                return;
            }


            const response = await paymentService.checkPaymentStatus(orgId, appId);

            if (response.success && response.data && Array.isArray(response.data) && response.data.length > 0) {
                console.log('Payment data received:', response.data);

                // Check if any payment has success status
                const hasSuccessfulPayment = response.data.some(payment => {
                    const hasOrderStatus = payment.order_status?.toLowerCase() === 'success' || payment.order_status === 'Success';

                    console.log(`Payment ${payment.Id}: order_status=${payment.order_status}`);

                    return hasOrderStatus
                });

                console.log('hasSuccessfulPayment:', hasSuccessfulPayment);

                // Check if any payment has failed status
                const hasFailedPayment = response.data.some(payment =>
                    payment.order_status?.toLowerCase() === 'failed'
                );
                if (hasSuccessfulPayment == true) {
                    console.log('Successful payment found:', response.data);
                    setPaymentStatus('success');
                    setShowPaymentButtons(false);

                    // Check if all fields are complete for submit button (use current data to avoid timing issues)
                    const hasAllFields = currentData.representativeName &&
                        currentData.representativeTitle &&
                        currentData.shareholderDirectorFile &&
                        currentData.finalDeclarationAgreed &&
                        currentData.termchk;

                    console.log('Checking submit button conditions with data:', currentData);
                    console.log('🔍 Final Submit Button Conditions:');
                    console.log('hasAllFields:', hasAllFields);
                    console.log('fieldsDisabled:', fieldsDisabled);
                    console.log('applicationStage:', applicationStage);
                    console.log('applicationStage === "":', applicationStage === "");
                    console.log('applicationStage === null:', applicationStage === null);
                    console.log('Stage condition result:', (applicationStage === "" || applicationStage === null));
                    console.log('Final condition result:', ((hasAllFields || fieldsDisabled) && (applicationStage === "" || applicationStage === null)));

                    if ((hasAllFields || fieldsDisabled) && (applicationStage === "" || applicationStage === null)) {
                        console.log('✅ SHOWING Final Submit Button');
                        setShowSubmitButton(true);
                    } else {
                        console.log('❌ HIDING Final Submit Button');
                        setShowSubmitButton(false);
                    }
                } else if (hasFailedPayment === true) {
                    console.log('Failed payment found:', response.data);
                    setPaymentStatus('failed');

                    // Check if all fields are complete (use current data to avoid timing issues)
                    const hasAllFields = currentData.representativeName &&
                        currentData.representativeTitle &&
                        currentData.shareholderDirectorFile &&
                        currentData.finalDeclarationAgreed &&
                        currentData.termchk;

                    if (hasAllFields || fieldsDisabled) {
                        setShowPaymentButtons(true);
                    } else {
                        setShowPaymentButtons(false);
                    }
                    setShowSubmitButton(false);
                } else {

                    setPaymentStatus('pending');
                    setShowPaymentButtons(false);
                    setShowSubmitButton(false);
                }
            } else {

                setPaymentStatus(null);

                // Check if all fields are complete (use current data to avoid timing issues)
                const hasAllFields = currentData.representativeName &&
                    currentData.representativeTitle &&
                    currentData.shareholderDirectorFile &&
                    currentData.finalDeclarationAgreed &&
                    currentData.termchk;

                if (hasAllFields || fieldsDisabled) {

                    setShowPaymentButtons(true);
                } else {

                }
                setShowSubmitButton(false);
            }
        } catch (error) {
            console.error('Error checking payment status:', error);
            setPaymentStatus(null);

            // Check if all fields are complete (use current data to avoid timing issues)
            const hasAllFields = currentData.representativeName &&
                currentData.representativeTitle &&
                currentData.shareholderDirectorFile &&
                currentData.finalDeclarationAgreed &&
                currentData.termchk;

            if (hasAllFields || fieldsDisabled) {
                setShowPaymentButtons(true);
            }
        }
    };


    const checkPaymentCount = async () => {
        try {
            const orgId = getOrgId();
            const appId = getAppId();

            if (!orgId || !appId) return;

            const response = await paymentService.checkPaymentCount(orgId, appId);

            console.log("count for payment", response);

            if (response.success && response.data && response.data.length > 0) {
                const proceedTime = new Date(response.data[0].ProceedDateTime);
                const currentTime = new Date();

                // Add 40 minutes
                const allowedTime = new Date(proceedTime.getTime() + 40 * 60000);

                console.log("Proceed Time:", proceedTime);
                console.log("Allowed Time:", allowedTime);
                console.log("Current Time:", currentTime);

                if (currentTime >= allowedTime) {
                    setCanProceedToPay(true);
                } else {
                    setCanProceedToPay(false);

                    // OPTIONAL: auto-enable after remaining time
                    const remainingTime = allowedTime - currentTime;

                    setTimeout(() => {
                        setCanProceedToPay(true);
                    }, remainingTime);
                }
            } else {
                // If no record → allow payment
                setCanProceedToPay(true);
            }
        } catch (error) {
            console.error("Error checking payment count:", error);
            setCanProceedToPay(true);
        }
    };





    // Auto-save when data changes (only if onUpdate is provided and data is meaningful)
    useEffect(() => {
        if (onUpdate && initialLoadComplete && Object.keys(declarationData).length > 0) {
            onUpdate(declarationData);
        }
    }, [declarationData, onUpdate, initialLoadComplete]);

    // Show notification helper
    const showNotification = (message, severity = 'info') => {
        setNotification({
            open: true,
            message,
            severity
        });
    };

    // Close notification
    const closeNotification = () => {
        setNotification(prev => ({ ...prev, open: false }));
    };

    // Handle checkbox changes
    const handleCheckboxChange = (fieldName) => (event) => {
        setDeclarationData(prev => ({
            ...prev,
            [fieldName]: event.target.checked
        }));
    };

    // Handle text field changes
    const handleTextFieldChange = (fieldName) => (event) => {
        setDeclarationData(prev => ({
            ...prev,
            [fieldName]: event.target.value
        }));
    };

    // Handle file upload - matching Laboratory section implementation
    const handleFileUpload = (fieldName, uploadData) => {
        if (uploadData === null || uploadData === undefined) {
            // File was deleted, remove from form data
            setDeclarationData(prev => ({
                ...prev,
                [fieldName]: null
            }));
            showNotification('Declaration file removed', 'info');
            return;
        }

        const fileData = {
            ...uploadData,
            fileName: uploadData.fileName || uploadData.originalName,
            filePath: uploadData.filePath || uploadData.url,
            uploadDate: new Date().toISOString(),
            fileSize: uploadData.fileSize,
            mimeType: uploadData.mimeType
        };

        setDeclarationData(prev => ({
            ...prev,
            [fieldName]: fileData
        }));

        if (uploadData && uploadData.fileName) {
            showNotification('Declaration file uploaded successfully', 'success');
        }

    };

    // Check if all required fields are filled for saving declaration data
    const canSaveDeclaration = () => {
        return declarationData.representativeName?.trim() &&
            declarationData.representativeTitle?.trim() &&
            declarationData.finalDeclarationAgreed &&
            declarationData.termchk &&
            declarationData.shareholderDirectorFile;
    };

    // Watch for changes in form data to enable/disable save button
    useEffect(() => {
        if (showSaveButton && !fieldsDisabled) {
            // Form is editable - update save button state based on completion
            const isComplete = canSaveDeclaration();
            // Save button will be enabled/disabled based on canSaveDeclaration()
        }
    }, [declarationData, showSaveButton, fieldsDisabled]);

    // Handle declaration submission
    const handleSubmitDeclaration = async () => {
        try {
            setLoading(true);

            // Basic validation - check if all required fields are filled
            if (!canSaveDeclaration()) {
                showNotification('Please complete all required declarations before submitting', 'error');
                setLoading(false);
                return;
            }

            const appId = getAppId();
            const orgId = getOrgId();
            const labtypeidfromstorage = localStorage.getItem('labTypeId')

            // Validate session data
            if (!appId || !orgId) {
                showNotification('Session data not found. Please login again.', 'error');
                setLoading(false);
                return;
            }

            // First submit the final declaration

            showNotification('Submitting application...', 'info');

            const result = await declarationService.submitFinalDeclaration(appId, orgId, labtypeidfromstorage);

            if (result.success) {
                showNotification('Application Submitted Successfully', 'success');
                setSubmitDialog(false);
                 // Send lab submission notification email
               const response = await axios.post(`${process.env.REACT_APP_API_URL}/assessment-mail/send-lab-submission-notification`,
                 { appId: appId, orgId: orgId } );
                console.log(response.data);
                // Update parent component if needed
                if (onUpdate) {
                    onUpdate({
                        ...declarationData,
                        isSubmitted: true,
                        submissionResult: result.data
                    });
                }


                showNotification('Redirecting to generate preview report...', 'info');

                // Add a small delay to ensure the user sees the success message
                setTimeout(() => {
                    navigate(`/main/testing-preview-report?appId=${encryptAppId(appId)}&orgId=${encryptText(orgId)}&observation=${encryptText('False')}&autoDownload=true&fromSubmission=true`);
                }, 1500);

                return;
            } else {
                throw new Error(result.message || 'Failed to submit declaration');
            }

        } catch (error) {
            console.error('Error submitting declaration:', error);
            showNotification(`Error submitting declaration: ${error.message}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    // Handle save declaration data
    const handleSaveDeclaration = async () => {
        try {
            setLoading(true);

            // Validate required fields before saving
            if (!canSaveDeclaration()) {
                const missingFields = [];
                if (!declarationData.representativeName?.trim()) missingFields.push('Representative Name');
                if (!declarationData.representativeTitle?.trim()) missingFields.push('Representative Title');
                if (!declarationData.finalDeclarationAgreed) missingFields.push('Final Declaration Agreement');
                if (!declarationData.termchk) missingFields.push('Terms & Conditions Agreement');
                if (!declarationData.shareholderDirectorFile) missingFields.push('Shareholders and Directors Declaration File');

                showNotification(`Please complete the following required fields: ${missingFields.join(', ')}`, 'error');
                return;
            }

            const orgId = getOrgId();
            const appId = getAppId();

            // Combine name and title into DeclNameOrg field
            const combinedNameTitle = `${declarationData.representativeName.trim()}, ${declarationData.representativeTitle.trim()}`;

            const saveData = {
                DeclNameOrg: combinedNameTitle,
                representativeName: declarationData.representativeName.trim(),
                representativeTitle: declarationData.representativeTitle.trim(),
                shareholderDirectorFile: declarationData.shareholderDirectorFile,
                termchk: declarationData.termchk,
                finalDeclarationAgreed: declarationData.finalDeclarationAgreed
            };

            const response = await LaboratoryService.saveDeclarationData(orgId, appId, saveData);

            if (response.success) {
                showNotification('Declaration data saved successfully', 'success');

                // After successful save, reload declaration data to update UI
                await loadDeclarationData();
            } else {
                showNotification(`Error saving declaration data: ${response.message}`, 'error');
            }

        } catch (error) {
            console.error('Error saving declaration data:', error);
            showNotification(`Error saving declaration data: ${error.message}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    // Handle preview report button click
    const handlePreviewReport = () => {
        const orgId = getOrgId();
        const appId = getAppId();

        if (!orgId || !appId) {
            showNotification('Organization ID or Application ID not found', 'error');
            return;
        }

        // Encrypt parameters before constructing URL
        const encryptedAppId = encryptAppId(appId);
        const encryptedOrgId = encryptText(orgId);
        const encryptedObservation = encryptText('false');

        const previewUrl = `/main/testing-preview-report?appId=${encryptedAppId}&orgId=${encryptedOrgId}&observation=${encryptedObservation}`;

        // Open in new tab/window
        window.open(previewUrl, '_blank');

        showNotification('Opening preview report in new tab', 'info');
    };

    // Handle proceed to payment button click
    const handleProceedToPay = () => {
        setPasswordDialog(true);
        setPassword('');
        setPasswordError('');
    };

    const handlePerformaInvoice = () => {
        const orgId = localStorage.getItem('orgId');
        const appId = getAppId();


        const previewUrl = `https://nablwpnew.qci.org.in/InvoiceApplicationPerfomaQR?appId=${appId}&payType=2`;

        // Open in new tab/window
        window.open(previewUrl, '_blank');

    };
    // Handle password verification using login password
    const handlePasswordVerification = async () => {
        try {
            setLoading(true);
            const orgId = getOrgId();

            if (!orgId) {
                setPasswordError('Organization ID not found. Please login again.');
                setLoading(false);
                return;
            }

            // Verify password using authService (matches your ASP.NET verifyPass method)
            const verificationResult = await paymentService.verifyPaymentPassword(orgId, password);


            if (verificationResult && verificationResult.success && verificationResult.isValid) {
                setPasswordDialog(false);
                setPasswordError(''); // Clear any previous errors

                // Navigate to payment page directly
                const encryptedPaymentType = encryptText('2');
                navigate(`/main/testing/payment?paymentType=${encryptedPaymentType}&appId=${encryptAppId(getAppId())}`);

                showNotification('Password verified. Redirecting to payment gateway...', 'success');
            } else {
                // Handle password verification failure
                const errorMessage = verificationResult?.message || 'Password is incorrect';
                setPasswordError(errorMessage);


            }
        } catch (error) {
            console.error('Password verification error:', error);

            // Handle different types of errors
            if (error.response && error.response.status === 500) {
                setPasswordError('Server error. Please try again later.');
            } else if (error.response && error.response.status === 404) {
                setPasswordError('User not found. Please check your organization ID.');
            } else if (error.message && error.message.includes('Network')) {
                setPasswordError('Network error. Please check your connection.');
            } else {
                setPasswordError('Password verification failed. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleGstExemption = async () => {
        try {
            setLoading(true);

            const appId = getAppId();
            if (!appId) return;

            const result = await LaboratoryService.gstexemption(appId);

            if (result?.success) {
                setGstExemptionStatus('pending');
                setGstExemptionMessage('Your GST exemption request has been submitted and is awaiting approval from the accounts section.');

                showNotification('GST exemption request submitted successfully', 'success');
            } else {
                showNotification('Failed to submit GST exemption request', 'error');
            }

        } catch (error) {
            console.error('GST exemption api not working', error);
            showNotification('Error submitting GST exemption request', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Handle password dialog close
    const handlePasswordDialogClose = () => {
        setPasswordDialog(false);
        setPassword('');
        setPasswordError('');
    };

    return (
      <Box sx={{ p: 3 }}>
        <Paper elevation={3} sx={{ p: 3 }}>
          {/* Header */}
          <Box sx={{ mb: 4, textAlign: "center" }}>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: "bold",
                color: "primary.main",
                mb: 2,
              }}
            >
              Declaration for NABL Accreditation
            </Typography>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              sx={{ mb: 2 }}
            >
              Terms & Conditions for Obtaining and Maintaining NABL
              Accreditation
            </Typography>

            {/* Completion Status Indicator */}
            <Alert severity="success" sx={{ maxWidth: "600px", mx: "auto" }}>
              <Typography variant="body2">
                ✅ All enclosure requirements have been completed. You can now
                proceed with the declaration.
              </Typography>
            </Alert>
          </Box>

          {/* Adequacy Check Section */}
          <Box sx={{ mb: 4 }}>
            <AdequacyCheckSection
              sectionId="declaration"
              title="Declaration Section"
              applicationStage={applicationStage}
              orgId={getOrgId()}
              appId={getAppId()}
              adequacyChecks={adequacyChecks}
              handleAdequacyCheck={handleAdequacyCheck}
              handleAdequacyRemarks={handleAdequacyRemarks}
              handleAdequacySubmit={handleAdequacySubmit}
              loading={adequacyLoading}
              currentUser={localStorage.getItem("userName") || "System User"}
              currentUserId={parseInt(localStorage.getItem("userId") || "0")}
            />
          </Box>

          {/* Terms & Conditions Content */}
          {/* <Card elevation={2} sx={{ mb: 4 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                            <GavelIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                            Terms & Conditions
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ textAlign: 'justify', lineHeight: 1.6 }}>
                            The terms and conditions mentioned in this document is considered as accreditation agreement between NABL and Conformity Assessment Body (CAB). By accepting these terms and conditions, it is implied that a CAB as an applicant and after obtaining accreditation agrees to comply at all times with all Terms and Conditions for NABL Accreditation. Any violation of this terms and conditions (Accreditation agreement) shall result in adverse decision as per NABL 216 'Procedures for Dealing with Adverse Decisions' including legal action.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ textAlign: 'justify', lineHeight: 1.6 }}>
                            The Conformity Assessment Body {'{Testing Laboratory/ Calibration Laboratory/ Medical Testing Laboratory/ Proficiency Testing Provider (PTP)/ Reference Material Producer (RMP)}'} that are applicant or accredited by NABL shall be required to fulfill the following terms and conditions:
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ textAlign: 'justify', lineHeight: 1.6 }}>
                            By accepting these terms and conditions, it is implied that a CAB as an applicant and after obtaining accreditation agrees to comply at all times with all Terms and Conditions for Obtaining and Maintaining NABL Accreditation. Any violation of this terms and conditions (arrangement) shall result in adverse decision as per NABL 216 'Procedures for Dealing with Adverse Decisions' including legal action.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ textAlign: 'justify', lineHeight: 1.6, fontWeight: 'bold', color: 'error.main' }}>
                            Dispute, if any, arising out of NABL decisions that remain unresolved through mechanism provided by NABL are subject to the exclusive jurisdiction of the Courts at New Delhi and none other.
                        </Typography>
                    </CardContent>
                </Card> */}

          <Card elevation={2} sx={{ mb: 4 }}>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: "primary.main", fontWeight: "bold" }}
              >
                <GavelIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                Terms & Conditions
              </Typography>

              <Typography
                variant="body1"
                paragraph
                sx={{ textAlign: "justify", lineHeight: 1.6 }}
              >
                The terms and conditions mentioned in this document are
                considered as accreditation agreement between NABL and
                Conformity Assessment Body (CAB). By accepting these terms and
                conditions, it is implied that a CAB as an applicant and after
                obtaining accreditation agrees to comply at all times with all
                the Terms and Conditions for maintaining NABL Accreditation. Any
                violation of any one or more of these terms and conditions
                (accreditation agreement) shall result in adverse decision as
                per NABL 216 'Procedures for Dealing with Adverse Decisions',
                including legal action.
              </Typography>

              <Typography
                variant="body1"
                paragraph
                sx={{ textAlign: "justify", lineHeight: 1.6 }}
              >
                The Conformity Assessment Body (Testing Laboratory/ Calibration
                Laboratory/ Medical Testing Laboratory/ Proficiency Testing
                Provider (PTP)/ Reference Material Producer (RMP)/ Biobank) that
                are applicant or accredited by NABL shall be required to fulfill
                the following terms and conditions:
              </Typography>

              <Box sx={{ pl: 2 }}>
                {/* Clause 1 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>1.</strong> The Conformity Assessment Body (CAB) shall
                  carry out its Testing / Calibration / Medical/ PTP /
                  RMP/Biobank activities in such a way as to meet the
                  requirements of relevant standard (eg. ISO/IEC 17025 or ISO
                  15189 or ISO/IEC 17043 or ISO 17034 or ISO 20387, whichever is
                  applicable), appropriate NABL specific criteria/application
                  documents (wherever applicable) and other policies of NABL, as
                  relevant to the accredited scope at all times.
                </Typography>

                {/* Clause 2 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>2.</strong> CAB shall ensure that the latest versions
                  of NABL documents are available with it and are effectively
                  implemented.
                </Typography>

                {/* Clause 3 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>3.</strong> CAB shall familiarize itself with all
                  relevant standards, NABL documents etc. applicable for
                  particular accreditation scheme.
                </Typography>

                {/* Clause 4 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>4.</strong> The CAB shall have a valid legal entity
                  status as given below:
                  <Box component="ul" sx={{ pl: 4, mt: 1 }}>
                    <li>
                      <strong>One Person Company:</strong> Registration
                      certificate under The Companies Act, 2013
                    </li>
                    <li>
                      <strong>Limited Liability Partnership:</strong>{" "}
                      Registration certificate under The Limited Liability
                      Partnership Act, 2008
                    </li>
                    <li>
                      <strong>Company:</strong> Registration certificate under
                      The Companies Act, 1956 or 2013
                    </li>
                    <li>
                      <strong>Societies / Trust:</strong> Registration
                      certificate under Societies Registration Act, 1860/
                      Registration under The Indian Trusts Act, 1882
                    </li>
                    <li>
                      <strong>Government:</strong> Gazette or Government
                      Notification or self-Declaration on Letter head by Head of
                      the organization
                    </li>
                  </Box>
                  <em>^For Medical Testing Laboratories refer NABL 153</em>
                </Typography>

                {/* Clause 5 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>5.</strong> CAB (Conformity Assessment Body) shall
                  provide all necessary information about its antecedents as
                  required by NABL which shall include but not limited to its
                  background information i.e. history, establishment, ownership,
                  and organizational structure, Key Personnel, Previous
                  Experience, Legal Compliance, Disputes and Complaints if any,
                  Quality Management System, Financial Information if required,
                  Conflict of Interest, Accreditations and Certifications from
                  any other body or Any other information, deemed necessary by
                  NABL to analyse and evaluate the CAB's eligibility for
                  accreditation. By submitting the application for
                  accreditation, the CAB expressly consents to the collection,
                  use, and disclosure to NABL of the provided information for
                  the purpose of evaluating the accreditation application and
                  ensuring compliance with accreditation standards. NABL
                  reserves the right to verify the accuracy and authenticity of
                  the provided information. Failure to provide accurate and
                  complete information or any attempt to withhold relevant
                  details shall result in the rejection of the accreditation
                  application.
                </Typography>

                {/* Clause 6 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>6.</strong> Accreditation granted by NABL signifies
                  conformity of the laboratory's management system and technical
                  competence with the applicable accreditation criteria at the
                  time of assessment and does not constitute an endorsement,
                  certification, warranty, or guarantee by NABL of the
                  laboratory's test or calibration results, services, or
                  continued performance.
                  <br />
                  NABL shall not be responsible or liable for the accuracy,
                  validity, or use of any test or calibration results, reports,
                  opinions, or services provided by the accredited laboratory,
                  nor for any contractual, commercial, or statutory obligations
                  between the laboratory and its clients or third parties.
                  <br />
                  Nothing contained herein shall be construed as limiting NABL's
                  functions, powers, or rights under the accreditation
                  agreement, nor as excluding liability arising from fraud or
                  willful misconduct or action specifically attributable to NABL
                  under the applicable law.
                </Typography>

                {/* Clause 7 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>7.</strong> Accreditation granted by NABL is voluntary
                  in nature and does not replace, waive, or substitute
                  compliance with any applicable law, rule, regulation, or
                  statutory requirement, except where such accreditation is
                  expressly mandated by a Government authority or regulatory
                  body.
                  <br />
                  In the event of any inconsistency, conflict, or contradiction
                  between the provisions of this accreditation agreement, or any
                  NABL guideline and the applicable laws, rules, regulations, or
                  statutory directions in force, the provisions of such
                  applicable law shall prevail to the extent of such
                  inconsistency.
                </Typography>

                {/* Clause 8 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>8.</strong> Accreditation granted by NABL is limited
                  solely to the assessment, recognition and attestation of the
                  technical competence of the Conformity Assessment Body (CAB)
                  in accordance with applicable accreditation criteria. Such
                  accreditation shall not be construed as a permission,
                  authorization, license, or approval to conduct any business,
                  commercial, clinical, diagnostic, or operational activity.
                </Typography>

                {/* Clause 9 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>9.</strong> It is the responsibility of CAB to obtain
                  and maintain statutory and regulatory approvals, licenses,
                  registrations and permissions as required under the applicable
                  laws and abide by the National/ Regional/ State/ Local
                  regulatory requirements/ Acts/ Rules/ Legal orders/ Court
                  Decisions/ Orders issued by Government/ Statutory Bodies as
                  applicable and effective from time to time.
                </Typography>

                {/* Clause 10 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>10.</strong> The CAB shall identify and define various
                  activities which they are involved in, and ensure that it does
                  not lead to any potential conflict of interest.
                </Typography>

                {/* Clause 11 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>11.</strong> The CAB shall have available qualified
                  and trained personnel for applied and/or accredited scope
                  including sampling (wherever applicable).
                </Typography>

                {/* Clause 12 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>12.</strong> Wherever applicable, the CAB shall follow
                  the test methods as mentioned in the current National or
                  International standards and/or as stipulated by regulatory
                  bodies. Where such methods do not exist, other validated
                  methods may be followed. In case CAB uses in-house validated
                  methods, the validation data should be submitted along with
                  the application.
                </Typography>

                {/* Clause 13 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>13.</strong> CAB shall ensure the capability with
                  respect to applied scope of accreditation (availability of
                  resources not limited to personnel, equipment, CRM, method
                  validation etc.) before applying to NABL and thereafter at all
                  times during the accredited period.
                </Typography>

                {/* Clause 14 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>14.</strong> The CAB shall promptly inform NABL of any
                  changes in its organization, personnel, facilities, scope of
                  services, or any other significant factors that may impact its
                  accreditation status.
                </Typography>

                {/* Clause 15 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>15.</strong> Applicant CAB shall successfully
                  participate in at least one Proficiency Testing (PT) program
                  prior to gaining accreditation in each discipline applied.
                  There may be situation where PT is not available. Requirements
                  are specified in NABL 163 and the same shall be followed for
                  PT participation or otherwise.
                </Typography>

                {/* Clause 16 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>16.</strong> CAB shall submit the completed
                  application for renewal of accreditation well in advance (give
                  sufficient time to process) prior to the expiry of
                  accreditation and also agree to undergo assessment as per the
                  schedule proposed by NABL to maintain continuity in
                  accreditation cycle. Application submitted after expiry of
                  accreditation will be considered as fresh application. The
                  assessment (onsite) interval shall not exceed 24 months and in
                  case if exceeds, then it will be treated afresh.
                </Typography>

                {/* Clause 17 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>17.</strong> The CAB shall offer full cooperation to
                  NABL or its representative or member of team
                  evaluating/assessing during the conduct of assessments
                  (including onsite surveillance), any additional assessments,
                  such as additional surveillance assessments based on risk,
                  unannounced assessment/visit (including visit for
                  investigation of complaints related to NABL accreditation
                  against the CAB). The cooperation by the CAB shall include
                  access to, but not limited to, the following activities:
                  <Box component="ol" sx={{ pl: 4, mt: 1 }} type="a">
                    <li>
                      Undergoing assessments in stipulated time intervals /
                      whenever NABL considered it as required.
                    </li>
                    <li>
                      Providing access to all areas of operations related to CAB
                      including permanent site, site, mobile / temporary,
                      subcontractor / external provider premises, wherever
                      relevant and applicable.
                    </li>
                    <li>
                      Undertaking any check / inspection to verify the
                      capability of the CAB for the applied and/or accredited
                      scope.
                    </li>
                    <li>
                      Witnessing the activities being performed relevant to
                      accreditation.
                    </li>
                    <li>
                      Assessing the competence of the staff (including staff
                      working in shift operations / at permanent site, at site,
                      at mobile facility) during assessment.
                    </li>
                    <li>
                      Access to all relevant information and documentation and
                      records.
                    </li>
                    <li>
                      Access to those documents that provide insight into the
                      level of independence and impartiality of the CAB from its
                      own activities and its related bodies, if applicable.
                    </li>
                    <li>
                      Access to all records pertaining to competence of the CAB
                      personnel.
                    </li>
                    <li>
                      Assessing the competence of the persons who are
                      responsible to report, review and authorization of the
                      results,
                    </li>
                    <li>
                      Assessing the competence of the persons who are
                      responsible for opinion and interpretation
                    </li>
                  </Box>
                </Typography>

                {/* Clause 18 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>18.</strong> The CAB shall maintain the highest level
                  of impartiality, integrity, and professionalism throughout the
                  accreditation process as well as accreditation period. It
                  shall avoid any actions or relationships that could compromise
                  its independence or objectivity.
                </Typography>

                {/* Clause 19 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>19.</strong> The CAB shall establish and maintain an
                  effective management system, adhering to recognized
                  international or any other standards by NABL, to ensure
                  consistent and reliable conformity assessment services.
                </Typography>

                {/* Clause 20 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>20.</strong> The CAB shall cooperate fully with NABL
                  during assessments, audits, investigations, and any other
                  activities related to the accreditation process. It shall
                  provide all necessary information and access required by NABL
                  to evaluate its compliance with accreditation standards.
                </Typography>

                {/* Clause 21 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>21.</strong> The CAB shall have, where applicable,
                  legally enforceable arrangements with their customer/client
                  that commit the customers/clients to provide, on request,
                  access to NABL assessment team to assess the CAB's performance
                  when carrying out conformity assessment activities at the
                  customer/client's site.
                </Typography>

                {/* Clause 22 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>22.</strong> An accredited CAB shall also demonstrate
                  the following during the validity period of its accreditation:
                  <Box component="ol" sx={{ pl: 4, mt: 1 }} type="a">
                    <li>compliance with the terms of this agreement</li>
                    <li>
                      continuing conformity with the relevant standard (eg.
                      ISO/IEC 17025 or ISO 15189 or ISO/IEC 17043 or ISO 17034
                      or ISO 20387, whichever is applicable) and appropriate
                      NABL specific criteria/application documents (wherever
                      applicable)
                    </li>
                    <li>
                      continuing competence within the scope of its
                      accreditation
                    </li>
                    <li>
                      willingness to give such undertakings as NABL may
                      reasonably require
                    </li>
                  </Box>
                </Typography>

                {/* Clause 23 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>23.</strong> During the accreditation period, the CAB
                  shall claim accreditation for only those premises, and scope
                  for which it has been accredited (as stated in Accreditation
                  Certificate and scope of Accreditation).
                </Typography>

                {/* Clause 24 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>24.</strong> Applicant CABs shall not claim to be NABL
                  Accredited or use NABL symbol/logo before receiving the
                  communication regarding grant of accreditation from NABL. In
                  case it is found that CAB is claiming to be NABL Accredited or
                  using NABL symbol/logo in applicant stage or misleading
                  customers about its accreditation status in any way, this may
                  lead to rejection of application or termination of the
                  assessment process or denial of accreditation. Legal action
                  may also be initiated.
                </Typography>

                {/* Clause 25 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>25.</strong> Accredited CAB shall adhere to 'NABL 133'
                  for the use of NABL symbol, use of NABL Accredited CAB
                  Combined ILAC MRA Mark as well as any claim of accreditation
                  of the CAB. CAB shall ensure that it fully conforms to the
                  requirements of NABL and ISO/IEC 17025 or ISO 15189 or ISO/IEC
                  17043 or ISO 17034 or ISO 20387 (as applicable) for claiming
                  accreditation status while making references of its NABL
                  accreditation in communication media. During the period of
                  suspension or after withdrawal of accreditation, suitable
                  legal action may be taken by NABL in case CAB falsely claims
                  to be NABL accredited.
                </Typography>

                {/* Clause 26 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>26.</strong> Accredited CAB shall ensure that the copy
                  of the test report/certificate, medical test
                  report/certificate, calibration certificate/report, PT report
                  and RM document stored/retained shall be an exact replica of
                  the report issued to the customer including the format of the
                  test report/certificate, medical test report/certificate,
                  calibration certificate/report, PT report and RM document
                  header & footer, and NABL symbol.
                </Typography>

                {/* Clause 27 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>27.</strong> CAB shall use NABL accredited CAB
                  Combined ILAC MRA Mark only after obtaining written permission
                  from NABL and the usage shall be restricted to the period of
                  validity of the permission as informed by NABL.
                </Typography>

                {/* Clause 28 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>28.</strong> Accredited CAB shall make it clear to its
                  customer that its activities falling under accredited scope in
                  no way imply that the product so tested or equipment
                  calibrated or PT scheme offered or Reference Material (RM)
                  produced is approved by NABL.
                </Typography>

                {/* Clause 29 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>29.</strong> The accredited CAB is neither allowed to
                  make misleading / unauthorized statement regarding its
                  accreditation nor refer to accreditation in such a way so as
                  to imply that a product, process, service, management system
                  or person is approved by NABL.
                </Typography>

                {/* Clause 30 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>30.</strong> The CAB shall pay application fees for
                  accreditation, renewal of accreditation, expenses towards
                  travel, boarding & lodging for any kind of assessment
                  including supplementary visit, on-site surveillance, desktop
                  surveillance, re-assessment and annual accreditation fees as
                  determined from time to time, by NABL.
                </Typography>

                {/* Clause 31 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>31.</strong> The CAB shall inform NABL without delay
                  (within 15 days) of significant changes relevant to
                  accreditation, such as:
                  <Box component="ol" sx={{ pl: 4, mt: 1 }} type="a">
                    <li>
                      its legal, commercial, ownership or organizational status,
                    </li>
                    <li>
                      its resources not limited to personnel, facilities,
                      equipment to manage and perform its activities, authorized
                      personnel responsible to report, review and authorization
                      of the results.
                    </li>
                    <li>premises,</li>
                    <li>scope of accreditation,</li>
                    <li>
                      External Service Provider/Subcontractor, Production and/or
                      characterization techniques (PTP/RMP),
                    </li>
                    <li>
                      Major changes in the operation of the CAB, for example,
                      closure of the CAB for long duration, say more than 6
                      months;
                    </li>
                    <li>
                      any other factor that may affect the ability of the CAB to
                      fulfill the requirements of NABL accreditation including
                      policies & ISO/IEC 17025, ISO 15189 or ISO/IEC 17043 or
                      ISO 17034 or ISO 20387 as applicable.
                    </li>
                    <li>
                      any other change in information submitted in application.
                    </li>
                  </Box>
                </Typography>

                {/* Clause 32 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>32.</strong> The CAB shall regularly visit NABL
                  website to keep itself updated with the latest versions of
                  NABL documents as well as the NABL newsletter or any other
                  relevant information concerning accreditation provided on the
                  NABL website.
                </Typography>

                {/* Clause 33 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>33.</strong> CAB shall always have a dedicated person
                  responsible for implementation of Management System.
                </Typography>

                {/* Clause 34 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>34.</strong> The CAB shall unambiguously and
                  truthfully declare the name of any individual and organization
                  that has provided consultancy for preparing towards NABL
                  accreditation, at the relevant section in the application
                  form.
                </Typography>

                {/* Clause 35 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>35.</strong> Where a CAB subcontracts a substantial or
                  critical part of the accredited activities that work shall be
                  placed with another CAB that is accredited by NABL.
                </Typography>

                {/* Clause 36 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>36.</strong> The accredited CAB shall commit to comply
                  with the changes initiated by NABL in its accreditation
                  criteria, policies and procedures. The CAB shall inform NABL
                  when such alterations under the agreed time frame have been
                  completed.
                </Typography>

                {/* Clause 37 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>37.</strong> The CAB shall take all necessary actions
                  and discharge all non-conformities raised during the
                  assessment within 30 days to avoid any adverse decisions
                  against it as detailed in 'NABL 216'. The same shall be
                  verified to the satisfaction of NABL. The final decision on
                  accreditation shall rest with NABL.
                </Typography>

                {/* Clause 38 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>38.</strong> CAB shall not use its NABL accreditation
                  in such a manner as to bring NABL into disrepute.
                </Typography>

                {/* Clause 39 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>39.</strong> At any point in the application or
                  initial process, if there is evidence of fraudulent behavior
                  and/ or if the CAB has intentionally provided false
                  information or conceals information, NABL shall reject the
                  application or terminate the assessment process or denial of
                  accreditation.
                </Typography>

                {/* Clause 40 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>40.</strong> If evidence is found of fraudulent
                  behaviour or the CAB intentionally provides false information
                  or conceals information, NABL shall initiate adverse action as
                  per NABL 216.
                </Typography>

                {/* Clause 41 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>41.</strong> The accredited CAB upon suspension,
                  withdrawal of accreditation or debar from reapplying (however
                  determined) shall forthwith discontinue claim of accreditation
                  in any form in all advertising material and return the
                  accreditation certificates to NABL, if applicable.
                </Typography>

                {/* Clause 42 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>42.</strong> CAB shall inform its affected clients of
                  the suspension, scope reduction, withdrawal of accreditation
                  (and debar from reapplying, if applicable) and the associated
                  consequences, without undue delay.
                </Typography>

                {/* Clause 43 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>43.</strong> Accredited CAB can voluntarily withdraw
                  accreditation by giving notice in writing to NABL (and by
                  surrendering the accreditation certificates, applicable) after
                  clearing the outstanding amount, if any.
                </Typography>

                {/* Clause 44 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>44.</strong> The CAB shall inform NABL, if any of the
                  proposed assessor(s) happens to be their consultant or
                  associated with the CAB in any other capacity (personal or
                  professional).
                </Typography>

                {/* Clause 45 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>45.</strong> NABL shall not be held responsible for
                  any legal or financial liability arising out of activities of
                  any of its accredited CAB involving in any accidental or
                  consequential damages to personnel / equipment / products at
                  any time.
                </Typography>

                {/* Clause 46 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>46.</strong> CAB shall not offer inducement of any
                  kind such as any gifts or any kind of payments in cash or any
                  undue favour to the assessment team members. In case of any
                  violation, NABL shall initiate adverse action as per NABL 216.
                </Typography>

                {/* Clause 47 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>47.</strong> All information obtained by NABL and its
                  personnel in NABL's direct dealing with the accredited CABs in
                  all stages from application onwards, other than the
                  information in public domain shall be treated as confidential.
                  When NABL is required by law or authorized by contractual
                  arrangements to release the confidential information, the CAB
                  shall be notified unless prohibited by law.
                </Typography>

                {/* Clause 48 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>48.</strong> CABs acknowledges and agrees to maintain
                  strict confidentiality with regard to all sensitive
                  information, records, data, and proprietary materials obtained
                  or accessed during the accreditation process. CABs shall not
                  disclose, share, or disseminate any confidential information
                  obtained from NABL or any other CABs, clients, or stakeholders
                  participating in the accreditation process. This includes, but
                  is not limited to, information related to assessment
                  methodologies, assessment outcomes, accreditation decisions,
                  and any other sensitive or proprietary information.
                </Typography>

                {/* Clause 49 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>49.</strong> If the CAB engages subcontractors,
                  consultants, or third-party service providers to assist in the
                  accreditation process, the CAB shall declare these to NABL and
                  ensure that such entities/individuals also comply with the
                  same level of confidentiality and other obligations as set
                  forth herein.
                </Typography>

                {/* Clause 50 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>50.</strong> The CAB shall implement appropriate
                  measures to protect the confidentiality and security of any
                  personal data or sensitive information received or processed
                  during the accreditation process. CAB shall ensure full
                  compliance with the provisions of the Digital Personal Data
                  Protection Act, 2023, and rules made thereunder, including
                  obligations relating to lawful processing, consent, purpose
                  limitation, data security, data retention, and data principal
                  rights.
                </Typography>

                {/* Clause 51 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>51.</strong> The CAB acknowledges that any breach of
                  the confidentiality obligations outlined in this clause may
                  result in the termination of the accreditation process, and
                  may also give rise to legal liabilities.
                </Typography>

                {/* Clause 52 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>52.</strong> The confidentiality obligations outlined
                  in this clause shall continue to apply even after the
                  conclusion of the accreditation process, regardless of whether
                  accreditation is granted or not, except as required by law.
                </Typography>

                {/* Clause 53 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>53.</strong> Information about the conformity
                  assessment body obtained from sources other than the
                  conformity assessment body (e.g. complainant, regulators)
                  shall be confidential between the conformity assessment body
                  and the NABL. The provider (source) of this information is
                  confidential to NABL and is not be shared with the conformity
                  assessment body, unless agreed by the source.
                </Typography>

                {/* Clause 54 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>54.</strong> The CAB shall indemnify and hold NABL,
                  its CEO, officers, directors, employees, representatives
                  protected and indemnified against any claims, demands, losses,
                  damages, penalties, costs, or legal proceedings arising out of
                  or in connection with any non-compliance by the CAB with
                  applicable laws, rules or regulations including the Digital
                  Personal Data Protection Act, 2023, or any act, omission,
                  negligence in the conduct of testing, calibration, sampling,
                  inspection, certification related laboratory activities or
                  associated services or misuse, misrepresentation or
                  unauthorised reference to NABL accreditation or any activity
                  of the laboratory that is prejudicial to public interest,
                  public health or national interest.
                </Typography>

                {/* Clause 55 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>55.</strong> The CAB agrees that NABL shall not be
                  liable for any damages, losses, or expenses incurred by the
                  CAB due to the rejection or withdrawal of its accreditation
                  application or accreditation status, regardless of the reasons
                  for such rejection or withdrawal.
                </Typography>

                {/* Clause 56 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>56.</strong> NABL shall not be liable for any failure
                  or delay in performing its obligations under the accreditation
                  process due to force majeure events or circumstances beyond
                  its reasonable control.
                </Typography>

                {/* Clause 57 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>57.</strong> The CAB shall maintain adequate and
                  appropriate insurance coverage to protect itself against any
                  liabilities, claims, or damages that may arise during the
                  accreditation process.
                </Typography>

                {/* Clause 58 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>58.</strong> The CAB seeking accreditation hereby
                  agrees to abide by all applicable rules, regulations,
                  guidelines, and standards set forth by NABL and other relevant
                  authorities throughout the accreditation process and the
                  provision of conformity assessment services.
                </Typography>

                {/* Clause 59 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>59.</strong> The CAB shall ensure strict compliance
                  with the accreditation criteria, policies, procedures, and
                  guidelines issued by NABL. The CAB shall implement any
                  necessary corrective actions to address any non-conformities
                  identified during assessments or audits.
                </Typography>

                {/* Clause 60 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>60.</strong> After exhausting all remedies provided by
                  NABL to resolve any issue, the CAB may approach courts at New
                  Delhi and none other and shall be governed by the laws of
                  India.
                </Typography>

                {/* Clause 61 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>61.</strong> If the acts of the CAB is violative to
                  the terms and conditions of NABL policy, rules, regulations or
                  CAB is found indulged into some fraudulent and unethical
                  activities or any such activities which may adversely affect
                  to the integrity of NABL or is in violation of the
                  accreditation terms, then the NABL shall be at liberty to take
                  appropriate action against the CAB as per its policy, rules
                  and regulations which may extend to the removal of CABs from
                  the NABL website. In case a CAB has approached the court
                  against the decision or action of NABL, the accreditation
                  certificate (related to the legal entity), and if there are
                  multiple CABs accredited by NABL at different locations
                  belonging to the same legal entity, then each one of the
                  accredited CABs shall be remain removed from the NABL website.
                  <br />
                  <br />
                  The existing activities of all CABs (linked to that legal
                  entity which has approached court) shall be put on hold till
                  the pendency or resolution of the matter and considering the
                  gravity of the matter. CAB shall not submit any application
                  during the pendency of the court proceeding and any such
                  application if submitted shall not be entertained by NABL till
                  the outcome of the court proceedings and the application if
                  any submitted by CAB during such period shall stand rejected
                  by NABL.
                </Typography>

                {/* Clause 62 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>62.</strong> The CAB acknowledges and agrees that NABL
                  shall not be held responsible or liable, directly or
                  indirectly, for the technical competence, results, reports,
                  opinions, decisions, services, business practices, contractual
                  arrangements or any acts or omissions of the laboratory and/or
                  its clients. The laboratory further undertakes that NABL
                  accreditation shall not be represented, used or construed as
                  an endorsement, approval, supervision, guarantee or assumption
                  of responsibility by NABL for the laboratory's activities,
                  test or calibration results or compliance with statutory or
                  contractual obligations.
                </Typography>

                {/* Clause 63 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>63.</strong> In the event that a CAB is found or is
                  reasonably suspected to have engaged in activities that are
                  contrary to applicable law, NABL regulations, accreditation
                  criteria, public interest, public health, national interest or
                  data protection obligations, NABL shall be entitled, without
                  prejudice to any other rights or remedies available under law
                  or contract to initiate appropriate action including but not
                  limited to suspension, reduction of scope, withdrawal of
                  accreditation or debarment in accordance with NABL 216 and
                  other applicable NABL documents.
                </Typography>
                {/* Clause 64 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>64.</strong> 	Without prejudice to the aforesaid rights and remedies, NABL shall reserve the right to initiate appropriate contractual or legal action as may be necessary to enforce the terms of the accreditation agreement. This right shall be invoked where the acts or omissions of the CAB constitute a breach of contractual obligations, fraudulent activities, unethical practices, negligence, or misconduct which has caused prejudice to NABL’s legal position, reputation, or the integrity and credibility of the accreditation system.
                </Typography>
                {/* Clause 65 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>65.</strong> Repeat violation of terms & conditions and commitments given by CAB to NABL shall be dealt strictly as per NABL 216 and in accordance with the applicable law.
                </Typography>
                {/* Clause 66 */}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ textAlign: "justify", lineHeight: 1.6 }}
                >
                  <strong>66.</strong> The existing CAB owner(s) shall be responsible for all the related compliances and violations (if any) for the previous and current CAB application(s) processed by NABL.
                </Typography>
              </Box>

              <Typography
                variant="body1"
                paragraph
                sx={{ textAlign: "justify", lineHeight: 1.6, mt: 2 }}
              >
                By accepting these terms and conditions, it is implied that a
                CAB as an applicant and after obtaining accreditation agrees to
                comply at all times with all Terms and Conditions for Obtaining
                and Maintaining NABL Accreditation. Any violation of this terms
                and conditions (arrangement) shall result in adverse decision as
                per NABL 216 'Procedures for Dealing with Adverse Decisions'
                including legal action. NABL reserves the right to suspend or
                withdraw accreditation in case of established irregularities
                without show cause notice or opportunity for hearing under
                exceptional circumstances and for the reasons including but not
                limited to the non-compliance, misconduct, non- remedial action,
                non- payment of fee, legal and regulatory compliance,
                emergencies and public interest. NABL shall notify the CAB
                promptly of such decision and provide reasons for the
                suspension, or withdrawal, The CAB shall have the right to
                appeal the decision of suspension, or withdrawal, in accordance
                with the established appeal mechanism and procedures prescribed
                by NABL.
              </Typography>
            </CardContent>
          </Card>

          {/* Declaration by the Laboratory */}
          <Card elevation={2} sx={{ mb: 4 }}>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: "primary.main", fontWeight: "bold" }}
              >
                <AssignmentIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                Declaration by the Laboratory
              </Typography>

              <Typography
                variant="body1"
                paragraph
                sx={{ fontWeight: "bold", mb: 3 }}
              >
                We declare that:
              </Typography>

              <List sx={{ pl: 2 }}>
                <ListItem
                  sx={{ display: "list-item", listStyleType: "disc", pl: 0 }}
                >
                  <ListItemText>
                    <Typography
                      variant="body1"
                      sx={{ textAlign: "justify", lineHeight: 1.6 }}
                    >
                      We are familiar with the Terms & Conditions for Obtaining and Maintaining NABL Accreditation (NABL 131), which is enclosed and will abide by it. We have understood the requirements given in NABL 133.
                    </Typography>
                  </ListItemText>
                </ListItem>
                <ListItem
                  sx={{ display: "list-item", listStyleType: "disc", pl: 0 }}
                >
                  <ListItemText>
                    <Typography
                      variant="body1"
                      sx={{ textAlign: "justify", lineHeight: 1.6 }}
                    >
                      We have declared and submitted accurate and complete information in the enclosed “Declaration Form”.
                    </Typography>
                  </ListItemText>
                </ListItem>

                <ListItem
                  sx={{ display: "list-item", listStyleType: "disc", pl: 0 }}
                >
                  <ListItemText>
                    <Typography
                      variant="body1"
                      sx={{ textAlign: "justify", lineHeight: 1.6 }}
                    >
                     We agree to comply fully with ISO/IEC 17025: 2017 for the accreditation of laboratory.
                    </Typography>
                  </ListItemText>
                </ListItem>

                <ListItem
                  sx={{ display: "list-item", listStyleType: "disc", pl: 0 }}
                >
                  <ListItemText>
                    <Typography
                      variant="body1"
                      sx={{ textAlign: "justify", lineHeight: 1.6 }}
                    >
                      We agree to comply with accreditation procedures, pay all costs for pre-assessment (if opted), assessment, supplementary visit (if any), surveillance and reassessment irrespective of the result.
                    </Typography>
                  </ListItemText>
                </ListItem>

                <ListItem
                  sx={{ display: "list-item", listStyleType: "disc", pl: 0 }}
                >
                  <ListItemText>
                    <Typography
                      variant="body1"
                      sx={{ textAlign: "justify", lineHeight: 1.6 }}
                    >
                      We agree to co-operate with the assessment team appointed by NABL for examination of all relevant documents/records by them and their visits to those parts of the laboratory that are part of the scope of accreditation.
                    </Typography>
                  </ListItemText>
                </ListItem>

                <ListItem
                  sx={{ display: "list-item", listStyleType: "disc", pl: 0 }}
                >
                  <ListItemText>
                    <Typography
                      variant="body1"
                      sx={{ textAlign: "justify", lineHeight: 1.6 }}
                    >
                      We comply to all national, regional and local regulatory requirements for operating a laboratory.
                    </Typography>
                  </ListItemText>
                </ListItem>
              </List>
            </CardContent>
          </Card>

          {/* Authorized Representative */}
          <Card elevation={2} sx={{ mb: 4 }}>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: "primary.main", fontWeight: "bold" }}
              >
                <SecurityIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                Authorized Representative
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Representative Name"
                    value={declarationData.representativeName}
                    onChange={handleTextFieldChange("representativeName")}
                    variant="outlined"
                    required
                    disabled={fieldsDisabled}
                    error={
                      !fieldsDisabled &&
                      !declarationData.representativeName?.trim()
                    }
                    helperText={
                      !fieldsDisabled &&
                      !declarationData.representativeName?.trim()
                        ? "Representative name is required"
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Representative Title"
                    value={declarationData.representativeTitle}
                    onChange={handleTextFieldChange("representativeTitle")}
                    variant="outlined"
                    required
                    disabled={fieldsDisabled}
                    error={
                      !fieldsDisabled &&
                      !declarationData.representativeTitle?.trim()
                    }
                    helperText={
                      !fieldsDisabled &&
                      !declarationData.representativeTitle?.trim()
                        ? "Representative title is required"
                        : ""
                    }
                  />
                </Grid>
              </Grid>

              <Typography
                variant="body1"
                paragraph
                sx={{ mt: 2, textAlign: "justify" }}
              >
                <strong>{declarationData.representativeName}</strong>,{" "}
                <strong>{declarationData.representativeTitle}</strong>,
                has provided consultancy for preparing towards NABL accreditation. [Information regarding any individual or organization who provided consultancy (if any) for NABL accreditation shall be declared]. 
              </Typography>

              <Typography variant="body1" paragraph sx={{ fontWeight: "bold" }}>
                All information provided in this application are true.
              </Typography>
            </CardContent>
          </Card>

          {/* File Upload Section */}
          <Card elevation={2} sx={{ mb: 4 }}>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: "primary.main", fontWeight: "bold" }}
              >
                <DescriptionIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                Upload Declaration by the Laboratory Shareholders and Directors
                *
              </Typography>

              <Alert
                severity={
                  declarationData.shareholderDirectorFile
                    ? "success"
                    : "warning"
                }
                sx={{ mb: 2 }}
              >
                {declarationData.shareholderDirectorFile
                  ? "Declaration file uploaded successfully."
                  : "Please upload the declaration signed by laboratory shareholders and directors. This is required."}
              </Alert>

              <FileUploadComponent
                fieldName="shareholderDirectorFile"
                label="Declaration by Shareholders and Directors"
                orgId={getOrgId()}
                section="declaration-documents"
                required={true}
                accept=".pdf,.jpg,.jpeg,.png"
                maxSize={10}
                multiple={false}
                onChange={handleFileUpload}
                initialFile={declarationData.shareholderDirectorFile}
                disabled={fieldsDisabled}
              />

              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  size="small"
                  onClick={() => {
                    // Download the Declaration Form from public folder
                    const link = document.createElement("a");
                    link.href = `${process.env.PUBLIC_URL}/Declaration Form.docx`;
                    link.download = "Declaration Form.docx";
                    link.target = "_blank";
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    showNotification("Downloading Declaration Form...", "info");
                  }}
                >
                  Download Format
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Final Declaration */}
          <Card
            elevation={2}
            sx={{
              mb: 4,
              backgroundColor: declarationData.finalDeclarationAgreed
                ? "success.50"
                : "warning.50",
              borderLeft: "5px solid",
              borderLeftColor: declarationData.finalDeclarationAgreed
                ? "success.main"
                : "warning.main",
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  color: declarationData.finalDeclarationAgreed
                    ? "success.dark"
                    : "warning.dark",
                  fontWeight: "bold",
                }}
              >
                <WarningIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                Final Agreement *
              </Typography>

              {!declarationData.finalDeclarationAgreed && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  You must agree to the terms and conditions to proceed with
                  saving the declaration.
                </Alert>
              )}

              <FormControlLabel
                control={
                  <Checkbox
                    checked={declarationData.termchk}
                    onChange={handleCheckboxChange("termchk")}
                    color={declarationData.termchk ? "success" : "warning"}
                    required
                    disabled={fieldsDisabled}
                  />
                }
                label="I have read and understood all the terms and conditions mentioned above."
                sx={{ alignItems: "flex-start", mb: 2 }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={declarationData.finalDeclarationAgreed}
                    onChange={handleCheckboxChange("finalDeclarationAgreed")}
                    color={
                      declarationData.finalDeclarationAgreed
                        ? "success"
                        : "warning"
                    }
                    required
                    disabled={fieldsDisabled}
                  />
                }
                label="By accepting the above terms & conditions, it is implied that a CAB/ Sample Collection Centre/ Facility (ies) declared by medical CAB as an applicant and after obtaining accreditation agrees to comply at all times with all Terms and Conditions for NABL Accreditation. Any violation of this terms and conditions shall result in adverse decision such as abeyance, suspension, forced withdrawal etc against the CAB as specified in 'NABL 216'."
                sx={{ alignItems: "flex-start" }}
              />
            </CardContent>
          </Card>

          {/* Action Buttons - Simplified Logic */}
          {(() => {
            const shouldShowButtons =
              (applicationStage === null || applicationStage === "18a") &&
              (isGstExemption === false || gstExemptionStatus === "approved");
            return shouldShowButtons;
          })() && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              {/* Save Declaration Button - Show when fields are not complete or not saved yet */}
              {showSaveButton && (
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveDeclaration}
                  disabled={loading || !canSaveDeclaration()}
                  size="large"
                  color="primary"
                  sx={{
                    background:
                      "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
                    boxShadow: "0 3px 5px 2px rgba(33, 203, 243, .3)",
                  }}
                >
                  {loading ? "Saving..." : "Save Declaration"}
                </Button>
              )}

              {showPaymentButtons && (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<ViewIcon />}
                    onClick={handlePreviewReport}
                    disabled={loading}
                    size="large"
                    color="secondary"
                    sx={{
                      borderColor: "secondary.main",
                      "&:hover": {
                        borderColor: "secondary.dark",
                        backgroundColor: "secondary.50",
                      },
                    }}
                  >
                    Preview Report
                  </Button>

                  {canProceedToPay && (
                    <Button
                      variant="contained"
                      startIcon={<PaymentIcon />}
                      onClick={handleProceedToPay}
                      disabled={loading}
                      size="large"
                      color="success"
                      sx={{
                        background:
                          "linear-gradient(45deg, #4CAF50 30%, #8BC34A 90%)",
                        boxShadow: "0 3px 5px 2px rgba(76, 175, 80, .3)",
                      }}
                    >
                      Proceed to Pay
                    </Button>
                  )}
                  {!canProceedToPay && (
                    <Typography color="error">
                      You will be able to make the payment after 40 minutes of
                      your last attempt.
                    </Typography>
                  )}
                  <Button
                    variant="outlined"
                    startIcon={<ViewIcon />}
                    onClick={handlePerformaInvoice}
                    disabled={loading}
                    size="large"
                    color="primary"
                    sx={{
                      borderColor: "primary.main",
                      "&:hover": {
                        borderColor: "primary.dark",
                        backgroundColor: "primary.50",
                      },
                    }}
                  >
                    Proforma Invoice
                  </Button>
                </>
              )}
              {/* Submit Final Declaration Button - Show when payment is successful */}
              {showSubmitButton && (
                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={() => setSubmitDialog(true)}
                  disabled={loading}
                  size="large"
                  color="success"
                  sx={{
                    background:
                      "linear-gradient(45deg, #FF6B6B 30%, #4ECDC4 90%)",
                    boxShadow: "0 3px 5px 2px rgba(76, 175, 80, .3)",
                  }}
                >
                  Final Submit
                </Button>
              )}
            </Box>
          )}
          {/* GST Exemption Status Alert */}
          {(gstExemptionStatus === "pending" ||
            gstExemptionStatus === "rejected") && (
            <Alert
              severity={gstExemptionStatus === "rejected" ? "error" : "warning"}
              sx={{
                mb: 4,
                p: 3,
                backgroundColor:
                  gstExemptionStatus === "rejected" ? "#ffebee" : "#fff3cd",
                border: `1px solid ${gstExemptionStatus === "rejected" ? "#f48fb1" : "#ffeaa7"}`,
                borderRadius: 2,
                "& .MuiAlert-icon": {
                  fontSize: "2rem",
                },
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    color:
                      gstExemptionStatus === "rejected" ? "#c62828" : "#856404",
                  }}
                >
                  {gstExemptionStatus === "rejected"
                    ? "❌ GST Exemption Rejected"
                    : "⏳ GST Exemption Approval Pending"}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color:
                      gstExemptionStatus === "rejected" ? "#c62828" : "#856404",
                  }}
                >
                  {gstExemptionMessage}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color:
                      gstExemptionStatus === "rejected" ? "#c62828" : "#856404",
                    fontStyle: "italic",
                    mt: 1,
                  }}
                >
                  {gstExemptionStatus === "rejected"
                    ? "Please resubmit your GST exemption request with the necessary corrections based on the remarks above."
                    : "Please wait for the accounts section to approve your GST exemption request before proceeding with the application submission."}
                </Typography>
              </Box>
            </Alert>
          )}

          {/* GST Exemption Approved Status */}
          {gstExemptionStatus === "approved" && (
            <Alert
              severity="success"
              sx={{
                mb: 4,
                p: 2,
                mt: 2,
                backgroundColor: "#e8f5e8",
                border: "1px solid #4caf50",
                borderRadius: 2,
              }}
            >
              <Typography
                variant="body1"
                sx={{ fontWeight: "bold", color: "#2e7d32" }}
              >
                ✅ GST Exemption Approved
              </Typography>
              <Typography variant="body2" sx={{ color: "#2e7d32" }}>
                Your GST exemption has been approved. You can proceed with the
                application.
              </Typography>
            </Alert>
          )}
          {isGstExemption === true &&
            (gstExemptionStatus === "pending" ||
              gstExemptionStatus === "rejected" ||
              gstExemptionStatus === "not_requested") && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleGstExemption}
                sx={{ mt: 2 }}
              >
                Proceed for GST Exemption
              </Button>
            )}
          {isGstExemption === true && gstExemptionStatus === "pending" && (
            <Alert severity="warning" sx={{ mt: 3 }}>
              ⏳ Your GST exemption request has been submitted and is awaiting
              approval from the accounts section.
            </Alert>
          )}
          {/* GST Exemption Not Approved Message - Only show for labs that need GST exemption */}
          {isGstExemption === true &&
            (gstExemptionStatus === "pending" ||
              gstExemptionStatus === "rejected" ||
              gstExemptionStatus === "not_requested") && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                <Alert
                  severity={
                    gstExemptionStatus === "rejected" ? "error" : "info"
                  }
                  sx={{
                    maxWidth: "600px",
                    p: 3,
                    backgroundColor:
                      gstExemptionStatus === "rejected" ? "#ffebee" : "#e3f2fd",
                    border: `1px solid ${gstExemptionStatus === "rejected" ? "#f48fb1" : "#90caf9"}`,
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: "bold", textAlign: "center" }}
                  >
                    {gstExemptionStatus === "rejected"
                      ? "❌ Application Submission Pending"
                      : "⏳ Application Submission Disabled"}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ textAlign: "center", mt: 1 }}
                  >
                    {gstExemptionStatus === "rejected"
                      ? "Your GST exemption request has been rejected. Please resubmit with corrections before proceeding."
                      : gstExemptionStatus === "pending"
                        ? "All submission options are currently unavailable until GST exemption is approved by the accounts section."
                        : "Please click on a Proceed for GST exemption request first before proceeding with the application."}
                  </Typography>
                </Alert>
              </Box>
            )}
        </Paper>

        {/* Password Verification Dialog */}
        <Dialog
          open={passwordDialog}
          onClose={handlePasswordDialogClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              backgroundColor: "primary.main",
              color: "white",
            }}
          >
            <LockIcon />
            Payment Authorization Required
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Please enter the authorization password to proceed with payment:
            </Typography>
            <TextField
              fullWidth
              type="password"
              label="Login Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={!!passwordError}
              helperText={
                passwordError ||
                "Enter your login password to authorize payment"
              }
              variant="outlined"
              disabled={loading}
              onKeyPress={(e) => {
                if (e.key === "Enter" && !loading) {
                  handlePasswordVerification();
                }
              }}
              autoFocus
            />
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Enter the same password you use to login to your account. This
                ensures secure access to the payment gateway.
              </Typography>
            </Alert>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={handlePasswordDialogClose}
              color="inherit"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              onClick={handlePasswordVerification}
              variant="contained"
              disabled={!password.trim() || loading}
            >
              {loading ? "Verifying..." : "Verify & Proceed"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Submit Confirmation Dialog */}
        <Dialog
          open={submitDialog}
          onClose={() => setSubmitDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Submit Final Declaration</DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Are you sure you want to submit the final declaration? This action
              confirms your agreement to all terms and conditions.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Once submitted, you will not be able to modify the declaration.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSubmitDialog(false)} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmitDeclaration}
              variant="contained"
              color="success"
              disabled={loading}
            >
              {loading ? "Submitting..." : "Submit Declaration"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Notification Snackbar */}
        <Snackbar
          open={notification.open}
          autoHideDuration={6000}
          onClose={closeNotification}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={closeNotification}
            severity={notification.severity}
            sx={{ width: "100%" }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </Box>
    );
};

export default DeclarationSection;